import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  MessageSquare,
  Users,
  BarChart3,
  Globe,
  Shield,
  Cpu,
  ArrowRight,
  Check,
  ChevronDown,
  Star,
  Menu,
  X,
  Play,
  Gift,
} from "lucide-react";

function Counter({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        obs.disconnect();
        let start = 0;
        const step = to / 60;
        const t = setInterval(() => {
          start += step;
          if (start >= to) {
            setVal(to);
            clearInterval(t);
          } else setVal(Math.floor(start));
        }, 16);
      },
      { threshold: 0.5 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return (
    <span ref={ref}>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

function Particles() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: Math.random() * 3 + 1 + "px",
            height: Math.random() * 3 + 1 + "px",
            borderRadius: "50%",
            background: "rgba(26,158,92,0.4)",
            left: Math.random() * 100 + "%",
            top: Math.random() * 100 + "%",
            animation: `float ${Math.random() * 10 + 8}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + "s",
            opacity: Math.random() * 0.6 + 0.2,
          }}
        />
      ))}
    </div>
  );
}

const PLANS = [
  {
    name: "Starter",
    price: 9,
    desc: "Perfect for small businesses just getting started.",
    features: [
      "1 chatbot",
      "500 conversations/mo",
      "Lead capture",
      "Basic analytics",
      "Email support",
    ],
    cta: "Start free trial",
    popular: false,
    color: "#3d8ef0",
  },
  {
    name: "Business",
    price: 19,
    desc: "For growing teams that need more power.",
    features: [
      "5 chatbots",
      "5,000 conversations/mo",
      "Lead capture & CRM export",
      "Advanced analytics",
      "Custom branding",
      "Priority support",
    ],
    cta: "Start free trial",
    popular: true,
    color: "#1a9e5c",
  },
  {
    name: "Premium",
    price: 49,
    desc: "Enterprise-grade for high-volume operations.",
    features: [
      "Unlimited chatbots",
      "Unlimited conversations",
      "White-label widget",
      "API access",
      "Dedicated manager",
      "SLA guarantee",
    ],
    cta: "Start free trial",
    popular: false,
    color: "#d4882a",
  },
];

const FEATURES = [
  {
    icon: <Cpu size={22} />,
    title: "Powered by AI",
    desc: "Train your bot on your own content — FAQs, docs, URLs. It learns your business and answers like a pro.",
  },
  {
    icon: <Users size={22} />,
    title: "Lead Capture",
    desc: "Automatically collect visitor name, email, and phone. Every conversation is a sales opportunity.",
  },
  {
    icon: <BarChart3 size={22} />,
    title: "Deep Analytics",
    desc: "See what your visitors ask, when they drop off, and which bots convert best. Data-driven decisions.",
  },
  {
    icon: <Globe size={22} />,
    title: "Any Website",
    desc: "One line of code. Works on React, WordPress, Shopify, Webflow — any platform, any stack.",
  },
  {
    icon: <Shield size={22} />,
    title: "Always On",
    desc: "Your chatbot never sleeps. Respond to customers at 3am, on weekends, during holidays — automatically.",
  },
  {
    icon: <MessageSquare size={22} />,
    title: "Full Conversations",
    desc: "Review every chat, replay sessions, and understand exactly what your customers need from you.",
  },
];

const TESTIMONIALS = [
  {
    name: "Sarah M.",
    role: "Restaurant Owner",
    text: "Wispy captures customer inquiries even at midnight. I woke up to 3 new leads that booked tables — without lifting a finger.",
    avatar: "S",
  },
  {
    name: "James R.",
    role: "Real Estate Agent",
    text: "My website now works 24/7. Wispy qualifies leads automatically and books viewings directly into my calendar.",
    avatar: "J",
  },
  {
    name: "Dr. Ahmed K.",
    role: "Dental Clinic",
    text: "Patients can now book appointments through our chatbot at any hour. Our no-show rate dropped significantly.",
    avatar: "A",
  },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      style={{
        background: "var(--bg)",
        color: "var(--text)",
        fontFamily: "var(--font-body)",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(26,158,92,0.4)} 50%{box-shadow:0 0 0 16px rgba(26,158,92,0)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes videoPulse { 0%,100%{box-shadow:0 0 0 0 rgba(26,158,92,0.3)} 50%{box-shadow:0 0 40px 10px rgba(26,158,92,0.15)} }
        .fade-up-1 { animation: fadeUp 0.7s 0.1s ease both; }
        .fade-up-2 { animation: fadeUp 0.7s 0.2s ease both; }
        .fade-up-3 { animation: fadeUp 0.7s 0.3s ease both; }
        .fade-up-4 { animation: fadeUp 0.7s 0.4s ease both; }
        .landing-nav-link { color: var(--text2); text-decoration: none; font-size: 14px; font-weight: 500; transition: color .15s; }
        .landing-nav-link:hover { color: var(--text); }
        .feature-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 28px; transition: border-color .2s, transform .2s, box-shadow .2s; }
        .feature-card:hover { border-color: var(--brand); transform: translateY(-4px); box-shadow: 0 12px 40px rgba(26,158,92,0.12); }
        .testi-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 28px; transition: border-color .2s; }
        .testi-card:hover { border-color: var(--brand); }
        .pricing-card-land { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 32px; display: flex; flex-direction: column; gap: 24px; transition: transform .2s, box-shadow .2s; }
        .pricing-card-land:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.4); }
        .pricing-card-land.pop { border-color: var(--brand); background: linear-gradient(160deg,var(--surface),rgba(26,158,92,0.07)); }
        .hero-btn-primary { display:inline-flex;align-items:center;gap:8px;padding:14px 32px;background:var(--brand);color:#fff;border-radius:12px;font-size:16px;font-weight:600;text-decoration:none;transition:all .2s;font-family:var(--font-body);border:none;cursor:pointer; }
        .hero-btn-primary:hover { background:var(--brand-dark);transform:translateY(-2px);box-shadow:0 8px 32px rgba(26,158,92,0.35); }
        .demo-btn { display:inline-flex;align-items:center;gap:8px;padding:14px 32px;background:transparent;color:var(--text);border-radius:12px;font-size:16px;font-weight:600;text-decoration:none;transition:all .2s;border:1px solid var(--border2);font-family:var(--font-body);cursor:pointer; }
        .demo-btn:hover { background:var(--surface);border-color:var(--brand);color:var(--brand); }
        .glow-dot { width:10px;height:10px;background:var(--brand);border-radius:50%;animation:pulse 2s infinite; }
        .widget-preview { background:var(--bg2);border:1px solid var(--border2);border-radius:16px;overflow:hidden;box-shadow:0 24px 80px rgba(0,0,0,0.6); }
        .widget-header { background:var(--brand);padding:14px 18px;display:flex;align-items:center;gap:10px; }
        .widget-msg-bot { background:rgba(255,255,255,0.08);color:var(--text);padding:10px 14px;border-radius:12px;border-bottom-left-radius:3px;font-size:13px;max-width:85%;align-self:flex-start; }
        .widget-msg-user { background:var(--brand);color:#fff;padding:10px 14px;border-radius:12px;border-bottom-right-radius:3px;font-size:13px;max-width:85%;align-self:flex-end; }
        .code-snippet { background:var(--bg);border:1px solid var(--border2);border-radius:10px;padding:16px 20px;font-family:'Courier New',monospace;font-size:13px;color:#a8c5a0;line-height:1.7; }
        .section-label { display:inline-flex;align-items:center;gap:8px;background:rgba(26,158,92,0.1);border:1px solid rgba(26,158,92,0.25);color:var(--brand);padding:6px 14px;border-radius:20px;font-size:13px;font-weight:600;margin-bottom:20px; }
        .trial-banner { background:linear-gradient(90deg,rgba(26,158,92,0.15),rgba(26,158,92,0.05),rgba(26,158,92,0.15));border-bottom:1px solid rgba(26,158,92,0.2);padding:10px 5%;display:flex;align-items:center;justify-content:center;gap:12px;font-size:14px;font-weight:500; }
        .trial-badge { background:var(--brand);color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700;white-space:nowrap; }
        .avatar { width:44px;height:44px;border-radius:50%;background:var(--brand);display:flex;align-items:center;justify-content:center;font-weight:700;color:#fff;font-size:18px;flex-shrink:0; }
        .trial-box { background:linear-gradient(135deg,rgba(26,158,92,0.12),rgba(26,158,92,0.05));border:2px solid rgba(26,158,92,0.3);border-radius:16px;padding:24px 32px;display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;margin-bottom:48px; }
        .video-wrapper { border-radius:20px;overflow:hidden;border:2px solid rgba(26,158,92,0.4);box-shadow:0 32px 100px rgba(0,0,0,0.6);animation:videoPulse 3s ease-in-out infinite;position:relative; }
        .video-section { background:linear-gradient(180deg,var(--bg2) 0%,var(--bg) 100%);padding:100px 5%;border-top:1px solid var(--border);border-bottom:1px solid var(--border); }
        @media(max-width:768px){
          .hero-grid{grid-template-columns:1fr!important;}
          .features-grid{grid-template-columns:1fr!important;}
          .pricing-grid-land{grid-template-columns:1fr!important;}
          .testi-grid{grid-template-columns:1fr!important;}
          .stats-grid{grid-template-columns:repeat(2,1fr)!important;}
          .hide-mobile{display:none!important;}
          .hero-btns{flex-direction:column!important;align-items:stretch!important;}
          .hero-btns a,.hero-btns button{text-align:center;justify-content:center;}
          .trial-box{flex-direction:column;text-align:center;}
          .video-wrapper iframe{height:220px!important;}
        }
      `}</style>

      {/* ── Top trial banner ── */}
      <div className="trial-banner">
        <Gift size={16} color="var(--brand)" />
        <span style={{ color: "var(--text2)" }}>
          <strong style={{ color: "var(--text)" }}>Limited time:</strong> Start
          with a full
        </span>
        <span className="trial-badge">14-DAY FREE TRIAL</span>
        <span style={{ color: "var(--text2)" }}>— No credit card required</span>
        <Link
          to="/register"
          style={{
            color: "var(--brand)",
            fontWeight: 700,
            textDecoration: "none",
            fontSize: 13,
          }}
        >
          Claim now →
        </Link>
      </div>

      {/* ── Navbar ── */}
      <nav
        style={{
          position: "fixed",
          top: 40,
          left: 0,
          right: 0,
          zIndex: 999,
          background: scrolled ? "rgba(10,14,12,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          borderBottom: scrolled
            ? "1px solid var(--border)"
            : "1px solid transparent",
          transition: "all .3s",
          padding: "0 5%",
          height: 68,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <a
          href="/"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Zap size={20} fill="#1a9e5c" color="#1a9e5c" />
          <span
            style={{
              fontFamily: "var(--font-head)",
              fontSize: 22,
              fontWeight: 800,
              color: "var(--brand)",
            }}
          >
            Wispy
          </span>
        </a>
        <div
          className="hide-mobile"
          style={{ display: "flex", alignItems: "center", gap: 32 }}
        >
          <a href="#features" className="landing-nav-link">Features</a>
          <a href="#how" className="landing-nav-link">How it works</a>
          <a href="#demo" className="landing-nav-link">Demo</a>
          <a href="#pricing" className="landing-nav-link">Pricing</a>
          <a href="#testimonials" className="landing-nav-link">Reviews</a>
          <Link to="/blog" className="landing-nav-link">Blog</Link>
          <Link to="/faq" className="landing-nav-link">FAQ</Link>
        </div>
        <div
          className="hide-mobile"
          style={{ display: "flex", gap: 12, alignItems: "center" }}
        >
          <Link
            to="/login"
            style={{
              color: "var(--text2)",
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Sign in
          </Link>
          <Link
            to="/register"
            className="hero-btn-primary"
            style={{ padding: "10px 22px", fontSize: 14 }}
          >
            Start free trial
          </Link>
        </div>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            display: "none",
            background: "none",
            border: "none",
            color: "var(--text)",
            cursor: "pointer",
          }}
          className="show-mobile"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          style={{
            position: "fixed",
            top: 108,
            left: 0,
            right: 0,
            background: "var(--bg2)",
            borderBottom: "1px solid var(--border)",
            zIndex: 998,
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <a href="#features" className="landing-nav-link" onClick={() => setMenuOpen(false)}>Features</a>
          <a href="#pricing" className="landing-nav-link" onClick={() => setMenuOpen(false)}>Pricing</a>
          <a href="#testimonials" className="landing-nav-link" onClick={() => setMenuOpen(false)}>Reviews</a>
          <Link to="/login" className="landing-nav-link" onClick={() => setMenuOpen(false)}>Sign in</Link>
          <Link to="/register" className="hero-btn-primary" onClick={() => setMenuOpen(false)} style={{ textAlign: "center", justifyContent: "center" }}>
            Start free trial
          </Link>
        </div>
      )}

      {/* ── Hero ── */}
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          padding: "140px 5% 80px",
        }}
      >
        <Particles />
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(26,158,92,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          className="hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 64,
            alignItems: "center",
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <div>
            <div className="section-label fade-up-1">
              <div className="glow-dot" />
              AI-Powered Chat for Your Website
            </div>
            <h1
              className="fade-up-2"
              style={{
                fontFamily: "var(--font-head)",
                fontSize: "clamp(36px, 5vw, 62px)",
                fontWeight: 800,
                lineHeight: 1.1,
                marginBottom: 24,
                background: "linear-gradient(135deg, var(--text) 40%, var(--brand) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Turn Visitors Into
              <br />
              Customers While
              <br />
              You Sleep
            </h1>
            <p
              className="fade-up-3"
              style={{
                fontSize: 18,
                color: "var(--text2)",
                lineHeight: 1.7,
                marginBottom: 36,
                maxWidth: 480,
              }}
            >
              Deploy an AI chatbot on your website in minutes. Capture leads,
              answer questions, and support customers 24/7 — no coding required.
            </p>

            <div
              className="fade-up-3"
              style={{
                background: "rgba(26,158,92,0.08)",
                border: "1px solid rgba(26,158,92,0.25)",
                borderRadius: 12,
                padding: "14px 20px",
                marginBottom: 28,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Gift size={20} color="var(--brand)" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
                  14-day free trial included
                </div>
                <div style={{ fontSize: 13, color: "var(--text3)" }}>
                  No credit card · No commitment · Cancel anytime
                </div>
              </div>
            </div>

            <div className="fade-up-4 hero-btns" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <Link to="/register" className="hero-btn-primary" style={{ fontSize: 17, padding: "15px 36px" }}>
                Start free trial <ArrowRight size={18} />
              </Link>
              <a href="#demo" className="demo-btn" style={{ fontSize: 17, padding: "15px 36px" }}>
                <Play size={16} fill="currentColor" /> Watch demo
              </a>
            </div>

            <div
              className="fade-up-4"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                marginTop: 24,
                flexWrap: "wrap",
              }}
            >
              {["No credit card", "2-min setup", "Cancel anytime"].map((t) => (
                <div
                  key={t}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    color: "var(--text3)",
                  }}
                >
                  <Check size={14} color="var(--brand)" strokeWidth={3} />
                  {t}
                </div>
              ))}
            </div>
          </div>

          {/* Right — Widget preview */}
          <div className="fade-up-3 hide-mobile" style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ position: "relative", width: 320 }}>
              <div
                style={{
                  position: "absolute",
                  inset: -20,
                  background: "radial-gradient(circle, rgba(26,158,92,0.15) 0%, transparent 70%)",
                  borderRadius: "50%",
                }}
              />
              <div className="widget-preview">
                <div className="widget-header">
                  <span style={{ fontSize: 22 }}>💬</span>
                  <div>
                    <div style={{ fontWeight: 700, color: "#fff", fontSize: 14 }}>Wispy Assistant</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 6, height: 6, background: "#4ade80", borderRadius: "50%", display: "inline-block" }} />
                      Online
                    </div>
                  </div>
                </div>
                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, minHeight: 220 }}>
                  <div className="widget-msg-bot" style={{ animation: "fadeUp 0.5s 0.2s both" }}>
                    👋 Hi! How can I help you today?
                  </div>
                  <div className="widget-msg-user" style={{ animation: "fadeUp 0.5s 0.6s both" }}>
                    What plans do you offer?
                  </div>
                  <div className="widget-msg-bot" style={{ animation: "fadeUp 0.5s 1s both" }}>
                    We have 3 plans from $9/mo — all include a 14-day free trial! 🚀
                  </div>
                </div>
                <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "var(--text3)" }}>
                    Type a message…
                  </div>
                  <div style={{ width: 36, height: 36, background: "var(--brand)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ArrowRight size={16} color="#fff" />
                  </div>
                </div>
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: -24,
                  right: -16,
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "var(--brand)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 20px rgba(26,158,92,0.5)",
                  animation: "pulse 2.5s infinite",
                }}
              >
                <MessageSquare size={22} color="#fff" />
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            color: "var(--text3)",
            fontSize: 12,
          }}
        >
          <span>Scroll to explore</span>
          <ChevronDown size={16} style={{ animation: "float 2s ease-in-out infinite" }} />
        </div>
      </section>

      {/* ── Stats ── */}
      <section
        style={{
          padding: "60px 5%",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg2)",
        }}
      >
        <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 28, letterSpacing: ".05em", textTransform: "uppercase", fontWeight: 600 }}>
            What you get with every plan
          </p>
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 32 }}>
            {[
              { val: "2 min", label: "Average setup time" },
              { val: "24/7", label: "Always-on support bot" },
              { val: "1 line", label: "Of code to embed" },
              { val: "14 days", label: "Free trial, no card" },
            ].map(({ val, label }) => (
              <div key={label}>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 36, fontWeight: 800, color: "var(--brand)" }}>{val}</div>
                <div style={{ fontSize: 14, color: "var(--text3)", marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ padding: "100px 5%" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="section-label" style={{ margin: "0 auto 20px" }}>Everything you need</div>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, marginBottom: 16 }}>
              Built for results, not just chat
            </h2>
            <p style={{ color: "var(--text2)", fontSize: 17, maxWidth: 520, margin: "0 auto" }}>
              Every feature is designed to turn your website traffic into real business outcomes.
            </p>
          </div>
          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {FEATURES.map(({ icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div style={{ width: 48, height: 48, background: "rgba(26,158,92,0.1)", border: "1px solid rgba(26,158,92,0.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand)", marginBottom: 18 }}>
                  {icon}
                </div>
                <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{title}</h3>
                <p style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how" style={{ padding: "100px 5%", background: "var(--bg2)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="section-label" style={{ margin: "0 auto 20px" }}>Simple setup</div>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800 }}>Live in 3 steps</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 40 }} className="features-grid">
            {[
              { step: "01", title: "Create your bot", desc: "Sign up, give your bot a name and personality. Train it with your FAQs, docs, or website URL." },
              { step: "02", title: "Embed on your site", desc: "Copy one line of HTML and paste it into your website. Works on any platform in under 60 seconds." },
              { step: "03", title: "Watch it work", desc: "Your bot starts chatting with visitors immediately. Check your dashboard to see leads rolling in." },
            ].map(({ step, title, desc }, i) => (
              <div key={step} style={{ position: "relative" }}>
                {i < 2 && (
                  <div className="hide-mobile" style={{ position: "absolute", top: 28, left: "calc(100% + 12px)", width: "calc(100% - 24px)", height: 1, background: "linear-gradient(90deg, var(--brand), transparent)", opacity: 0.3 }} />
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 48, fontWeight: 800, color: "rgba(26,158,92,0.15)", lineHeight: 1 }}>{step}</div>
                  <h3 style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 700 }}>{title}</h3>
                  <p style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Demo Video ── */}
      <section id="demo" className="video-section">
        <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          <div className="section-label" style={{ margin: "0 auto 20px" }}>
            <Play size={13} fill="var(--brand)" /> See it in action
          </div>
          <h2
            style={{
              fontFamily: "var(--font-head)",
              fontSize: "clamp(28px, 4vw, 48px)",
              fontWeight: 800,
              marginBottom: 16,
              background: "linear-gradient(135deg, var(--text) 40%, var(--brand) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Set up your chatbot in 2 minutes
          </h2>
          <p style={{ color: "var(--text2)", fontSize: 18, marginBottom: 16, maxWidth: 560, margin: "0 auto 20px" }}>
            Watch how easy it is to create and embed your AI chatbot on any website — no coding needed.
          </p>

          {/* Stats row above video */}
          <div style={{ display: "flex", justifyContent: "center", gap: 32, marginBottom: 40, flexWrap: "wrap" }}>
            {[
              { val: "2 min", label: "to set up" },
              { val: "1 line", label: "of code" },
              { val: "24/7", label: "always on" },
            ].map(({ val, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--brand)" }}>{val}</div>
                <div style={{ fontSize: 13, color: "var(--text3)" }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Video */}
          <div className="video-wrapper">
            <iframe
              width="100%"
              height="560"
              src="https://www.youtube.com/embed/n4pdxpDUsv8?rel=0&modestbranding=1"
              title="How to Add an AI Chatbot to Your Website in 2 Minutes — Wispy AI"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ display: "block" }}
            />
          </div>

          {/* CTA below video */}
          <div style={{ marginTop: 40, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/register" className="hero-btn-primary" style={{ fontSize: 17, padding: "15px 36px" }}>
              Try it free — no credit card <ArrowRight size={18} />
            </Link>
            <a href="https://drift-world.com" target="_blank" rel="noopener noreferrer" className="demo-btn" style={{ fontSize: 17, padding: "15px 36px" }}>
              <Play size={16} fill="currentColor" /> Try live demo
            </a>
          </div>
          <p style={{ color: "var(--text3)", fontSize: 13, marginTop: 16 }}>
            14-day free trial · No credit card · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section id="testimonials" style={{ padding: "100px 5%" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="section-label" style={{ margin: "0 auto 20px" }}>
              <Star size={13} fill="var(--brand)" /> Real results
            </div>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, marginBottom: 16 }}>
              Businesses love Wispy
            </h2>
            <p style={{ color: "var(--text2)", fontSize: 17, maxWidth: 480, margin: "0 auto" }}>
              See how businesses are using Wispy to capture more leads and serve customers 24/7.
            </p>
          </div>
          <div className="testi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {TESTIMONIALS.map(({ name, role, text, avatar }) => (
              <div key={name} className="testi-card" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", gap: 4 }}>
                  {[...Array(5)].map((_, i) => (<Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />))}
                </div>
                <p style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.7, flex: 1 }}>"{text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="avatar">{avatar}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
                    <div style={{ fontSize: 12, color: "var(--text3)" }}>{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ padding: "100px 5%", background: "var(--bg2)", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="trial-box">
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 52, height: 52, background: "rgba(26,158,92,0.15)", border: "1px solid rgba(26,158,92,0.3)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Gift size={24} color="var(--brand)" />
              </div>
              <div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800 }}>14-Day Free Trial on Every Plan</div>
                <div style={{ color: "var(--text3)", fontSize: 14, marginTop: 4 }}>No credit card required · Full access · Cancel anytime · No risk</div>
              </div>
            </div>
            <Link to="/register" className="hero-btn-primary">Start your free trial <ArrowRight size={18} /></Link>
          </div>

          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="section-label" style={{ margin: "0 auto 20px" }}>Simple pricing</div>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, marginBottom: 12 }}>
              Start free, scale as you grow
            </h2>
            <p style={{ color: "var(--text2)", fontSize: 16 }}>All plans include the full 14-day free trial. No credit card required to start.</p>
          </div>

          <div className="pricing-grid-land" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {PLANS.map(({ name, price, desc, features, cta, popular, color }) => (
              <div key={name} className={`pricing-card-land${popular ? " pop" : ""}`} style={{ position: "relative" }}>
                {popular && (
                  <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "var(--brand)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 20, whiteSpace: "nowrap", letterSpacing: ".05em" }}>
                    MOST POPULAR
                  </div>
                )}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
                    <span style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18 }}>{name}</span>
                  </div>
                  <p style={{ color: "var(--text3)", fontSize: 13 }}>{desc}</p>
                </div>
                <div>
                  <span style={{ fontFamily: "var(--font-head)", fontSize: 48, fontWeight: 800 }}>${price}</span>
                  <span style={{ color: "var(--text3)", fontSize: 15 }}>/month</span>
                  <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(26,158,92,0.1)", border: "1px solid rgba(26,158,92,0.2)", borderRadius: 20, padding: "3px 10px", fontSize: 12, color: "var(--brand)", fontWeight: 600, marginLeft: 8 }}>
                    <Gift size={11} /> 14 days free
                  </div>
                </div>
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                  {features.map((f) => (
                    <li key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--text2)" }}>
                      <Check size={15} color="var(--brand)" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 24px", borderRadius: 12, fontWeight: 600, fontSize: 15, textDecoration: "none", transition: "all .2s", background: popular ? "var(--brand)" : "var(--surface2)", color: popular ? "#fff" : "var(--text)", border: popular ? "none" : "1px solid var(--border2)" }}>
                  {cta} <ArrowRight size={16} />
                </Link>
                <div style={{ textAlign: "center", fontSize: 12, color: "var(--text3)" }}>No credit card required</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: "100px 5%", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(26,158,92,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 640, margin: "0 auto", position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(26,158,92,0.1)", border: "1px solid rgba(26,158,92,0.25)", color: "var(--brand)", padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 600, marginBottom: 24 }}>
            <Gift size={13} /> 14-day free trial — no credit card
          </div>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 800, marginBottom: 20, lineHeight: 1.15 }}>
            Ready to grow with AI?
          </h2>
          <p style={{ color: "var(--text2)", fontSize: 17, marginBottom: 40, lineHeight: 1.7 }}>
            Start your free trial today. No credit card, no commitment — just results.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/register" className="hero-btn-primary" style={{ fontSize: 17, padding: "15px 36px" }}>
              Start free trial <ArrowRight size={20} />
            </Link>
            <a href="https://drift-world.com" target="_blank" rel="noopener noreferrer" className="demo-btn" style={{ fontSize: 17, padding: "15px 36px" }}>
              <Play size={16} fill="currentColor" /> Try live demo
            </a>
          </div>
          <p style={{ color: "var(--text3)", fontSize: 13, marginTop: 24 }}>
            No credit card required · Cancel anytime · Full 14-day access
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ borderTop: "1px solid var(--border)", padding: "40px 5%", background: "var(--bg2)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Zap size={18} fill="var(--brand)" color="var(--brand)" />
            <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, color: "var(--brand)", fontSize: 18 }}>Wispy</span>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {["#features", "#pricing", "#testimonials"].map((href) => (
              <a key={href} href={href} style={{ color: "var(--text3)", textDecoration: "none", fontSize: 13 }}>
                {href.replace("#", "").charAt(0).toUpperCase() + href.replace("#", "").slice(1)}
              </a>
            ))}
            <Link to="/login" style={{ color: "var(--text3)", textDecoration: "none", fontSize: 13 }}>Login</Link>
            <Link to="/register" style={{ color: "var(--text3)", textDecoration: "none", fontSize: 13 }}>Sign up</Link>
            <Link to="/blog" style={{ color: "var(--text3)", textDecoration: "none", fontSize: 13 }}>Blog</Link>
            <Link to="/faq" style={{ color: "var(--text3)", textDecoration: "none", fontSize: 13 }}>FAQ</Link>
          </div>
          <p style={{ color: "var(--text3)", fontSize: 13 }}>© 2025 Wispy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}